/* eslint-disable ember/no-computed-properties-in-native-classes */
import { readOnly } from '@ember/object/computed';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_ACTIVATE_ROLES = Object.freeze([GLOBAL_ADMIN]);

const CAN_CREATE_ROLES = Object.freeze([GLOBAL_ADMIN]);

const CAN_EDIT_DEVICE_SETTINGS_ROLES = Object.freeze([GLOBAL_ADMIN, LOCATION_ADMIN]);

const CAN_EDIT_NOTIFICATION_SETTINGS_ROLES = Object.freeze([GLOBAL_ADMIN, LOCATION_ADMIN]);

const CAN_EDIT_PICK_UP_SETTINGS = Object.freeze([GLOBAL_ADMIN, LOCATION_ADMIN]);

const CAN_VISIT_ROLES = Object.freeze([GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST]);

export default class DeliveryAreaAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  @readOnly('canActivate') canDisable!: boolean;
  @readOnly('state.deliveriesSubscription') deliveriesSubscription!: StateService['deliveriesSubscription'];

  get canActivate(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      // Only Global Admin can activate/disable delivery areas (requires create permission at company level)
      return (
        !!this.state.features?.canAccessDeliveriesApplication &&
        this.authz.hasPermissionAtCurrentCompany(Permission.DELIVERIES_DELIVERY_AREA_CREATE)
      );
    }

    const { roleNames: roles } = this.currentAdmin;
    const canActivateRoles = CAN_ACTIVATE_ROLES;

    return !!this.state.features?.canAccessDeliveriesApplication && isPresent(_intersection(canActivateRoles, roles));
  }

  get canCreate(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      // Global Admin, Location Admin, and Deliveries Admin can create delivery areas
      return (
        !!this.state.features?.canAccessDeliveriesApplication &&
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_AREA_CREATE)
      );
    }

    const { roleNames: roles } = this.currentAdmin;
    const canCreateRoles = CAN_CREATE_ROLES;

    return !!this.state.features?.canAccessDeliveriesApplication && isPresent(_intersection(canCreateRoles, roles));
  }

  get canGetStarted(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      // Only Global Admin can get started (requires create permission at company level)
      return this.authz.hasPermissionAtCurrentCompany(Permission.DELIVERIES_DELIVERY_AREA_CREATE);
    }

    const { roleNames: roles } = this.currentAdmin;
    return isPresent(_intersection(CAN_CREATE_ROLES, roles));
  }

  get canEditNotificationSettings(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_AREA_UPDATE);
    }

    const { roleNames: roles } = this.currentAdmin;
    return isPresent(_intersection(CAN_EDIT_NOTIFICATION_SETTINGS_ROLES, roles));
  }

  get canEditDeviceSettings(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_DEVICE_UPDATE);
    }

    const { roleNames: roles } = this.currentAdmin;
    return isPresent(_intersection(CAN_EDIT_DEVICE_SETTINGS_ROLES, roles));
  }

  get canEditPickUpSettings(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      return this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_AREA_UPDATE);
    }

    const { roleNames: roles } = this.currentAdmin;
    return isPresent(_intersection(CAN_EDIT_PICK_UP_SETTINGS, roles));
  }

  get canVisit(): boolean {
    if (this.featureFlags.isEnabled('cerbos-authz-deliveries-ui-permissions')) {
      // Can visit if user can create delivery areas OR read delivery settings
      // This excludes employees who only have read permission on delivery areas
      return (
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_AREA_CREATE) ||
        this.authz.hasPermissionAtCurrentLocation(Permission.DELIVERIES_DELIVERY_SETTINGS_READ)
      );
    }

    const { roleNames: roles } = this.currentAdmin;
    return isPresent(_intersection(CAN_VISIT_ROLES, roles));
  }
}
